import { create } from "zustand";
import { persist } from "zustand/middleware";
import { MOCK_WALLETS, WALLET_CREATION_EVENTS } from "@/data/mock";
import type { Wallet, WalletCreationEvent, WalletRequestValues } from "@/types";

interface PendingDetail {
  values: WalletRequestValues;
  events: WalletCreationEvent[];
  requestedBy: string;
  policy: string;
}

interface WalletState {
  wallets: Wallet[];
  pendingDetails: Record<string, PendingDetail>;
  counter: number;
  createWallet: (values: WalletRequestValues) => Wallet;
}

function buildEvents(values: WalletRequestValues, requestedBy: string): WalletCreationEvent[] {
  return [
    {
      title: "Configuration Submitted",
      meta: `Just now — by ${requestedBy}`,
      detail: `Label: ${values.name} · Type: ${values.type[0].toUpperCase()}${values.type.slice(1)} · Chain: ${values.network} · Provider: ${values.provider}`,
      state: "done",
    },
    {
      title: "Policy Evaluation Passed",
      meta: "Just now — POL-003 (Wallet Creation Policy)",
      detail: "Requires admin approval for new chain. Approval route: Marcus Webb or Hannah Lindqvist.",
      state: "done",
    },
    {
      title: "Pending Approval",
      meta: "Waiting since just now",
      detail: "Assigned to: Marcus Webb (USR-005) — SLA: 4h remaining",
      state: "active",
    },
    { title: "Provider Provisioning", meta: "Pending approval", state: "pending" },
  ];
}

export const useWalletStore = create<WalletState>()(
  persist(
    (set, get) => ({
      wallets: MOCK_WALLETS,
      pendingDetails: {
        "WLT-007": {
          values: {
            name: "HT Solana Ops",
            description: "",
            type: "hot",
            group: "Operations",
            tags: "",
            network: "Solana Mainnet",
            asset: "SOL",
            provider: "Mesh (VaultCore)",
          },
          events: WALLET_CREATION_EVENTS,
          requestedBy: "Priya Sharma (USR-003)",
          policy: "POL-003 (Wallet Creation)",
        },
      },
      counter: MOCK_WALLETS.length,
      createWallet: (values) => {
        const n = get().counter + 1;
        const id = `WLT-${String(n).padStart(3, "0")}`;
        const wallet: Wallet = {
          id,
          name: values.name,
          chain: values.network,
          provider: values.provider,
          type: values.type,
          balanceUsd: null,
          lastSyncAgo: null,
          status: "pending",
        };
        const requestedBy = "Priya Sharma (USR-003)";
        set((s) => ({
          wallets: [wallet, ...s.wallets],
          pendingDetails: {
            ...s.pendingDetails,
            [id]: { values, events: buildEvents(values, requestedBy), requestedBy, policy: "POL-003 (Wallet Creation)" },
          },
          counter: n,
        }));
        return wallet;
      },
    }),
    { name: "alloy-wallets" }
  )
);
